<template>
  <section class="box-layout">
    <div v-if="!isLoading" class="jam__content">
      <header class="content__header">
        <h2>{{ $t('userInfo.lnb.jam') }}
          <span>{{ $t('userInfo.managePlan.utcJamDesc')}}</span>
        </h2>
      </header>
      <div class="content__body" ref="scrollContent">
        <div class="content__top">
          <content-box class="jam-detail" :width="'50%'">
            <div class="jam-info__wrap">
              <div class="title">{{ $t('plan.jam.myJam') }}</div>
              <div class="jam" v-if="remainJam !== 0">
                <div class="icon icon--jam--85px"></div>
                <div class="count">
                <span class="count-value" v-auto-fit-text="{ min: 10, max: 45 }">
                  {{ totalRemainJam | thousand }}
                  <span class="unit">{{ $t('plan.jam.jamAmount') }}</span>
                </span>
                </div>
              </div>
              <div class="no-jam" v-else>
                <div v-if="$i18n.locale !== 'ja'" class="icon icon--empty-jam"></div>
                <div :style="{'padding-top': $i18n.locale === 'ja' ? '30px' : '5px'}" class="description" v-html="$t('plan.jam.emptyJam')"></div>
              </div>
              <div class="button--wrap">
                <ui-button primary4
                           class="button"
                           :disabled="!planData.hasPlan"
                           :iconName="'icon--plus-red'"
                           @click="onChargeJam()">
                  {{ $t('plan.jam.chargeJam') }}
                </ui-button>
              </div>
            </div>
            <div class="jam-heatmap__wrap">
              <div class="title" @click="onShift">
                <i class="icon icon--filter"></i>
                <span>{{ $t('plan.jam.heatmapJam.title') }}</span>
              </div>
              <div class="heatmap-count">
                <div class="description"><span class="title">{{ $t(`plan.jam.heatmapJam.description.title`) }}</span>
                  <ui-icon-tooltip :iconSize="'white-size17'" arrowBottom arrowCenter>
                    <span v-html="$t('plan.jam.heatmapJam.description.tooltip')"></span>
                  </ui-icon-tooltip>
                </div>
                <div class="info">
                  <span class="amount">{{ applyBasketCount | thousand }}</span>
                  <span class="count">{{ $t('plan.jam.jamAmount') }}</span>
                  <ui-tooltip arrowBottom arrowCenter>
                    <span>{{ applyBasketCount * 1000 | thousand}} PV {{ $t('common.availablePV') }}</span>
                  </ui-tooltip>
                </div>
                <div class="use-add">
                  <div class="use" v-if="inRemainPV">
                    <i class="icon icon--refresh-magenta"></i>
                    <span>{{ $t('common.inuse') }}</span>

                    <ui-tooltip arrowBottom arrowCenter>
                      <span class="remain">{{ inRemainPV | thousand}} PV </span>
                      <span class="total"> / {{ inChargePV | thousand}} PV</span>
                    </ui-tooltip>
                  </div>
                  <div class="add">
                    <button class="jam-add" @click="onShift">
                      <i class="icon icon--plus-black"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </content-box>
          <content-box v-if="expireJamList.length > 0" class="expiration-detail" :width="'50%'">
            <div class="title">{{ $t('plan.jam.jamExpirationGuide') }}</div>
            <div class="alert">{{ $t('plan.jam.jamExpirationAlert') }}</div>
            <div class="table">
              <table>
                <colgroup>
                  <col style="width: 33%">
                  <col style="width: 33%">
                  <col style="width: 33%">
                </colgroup>
                <thead>
                <tr>
                  <th>{{ $t('plan.jam.jamExpiration') }}</th>
                  <th>{{ $t('plan.jam.remainJam') }}</th>
                  <th>{{ $t('plan.jam.jamChargedDate') }}</th>
                </tr>
                </thead>
              </table>
              <div class="tbody">
                <template v-for="(item, index) in expireJamList">
                  <div class="tr" :key="index" style="display: flex;">
                    <div class="td">{{ item.expireDate | dashDate }}</div>
                    <div class="td">{{ item.jamChargeCount - item.jamUseCount | thousand }}</div>
                    <div class="td">{{ item.paymentDate | dashDate }}</div>
                  </div>
                </template>
              </div>
            </div>
          </content-box>
          <content-box v-else class="expiration-detail" :width="'50%'">
            <div class="title">{{ $t('plan.jam.jamExpirationGuide') }}</div>
            <div class="description">{{ $t('plan.jam.noExpiredJam') }}</div>
          </content-box>
        </div>
        <div class="content__bottom">
          <div class="chart-type">
            <button :class="{ on: this.currentChart === 'charge' }"
                    @click="onSelectChartType('charge')">
              {{ $t('plan.jam.jamChargeHistory') }}
            </button>
            <button :class="{ on: this.currentChart === 'use' }"
                    @click="onSelectChartType('use')">
              {{ $t('plan.jam.jamUseHistory') }}
            </button>

            <span class="jam-expired-date-desc">{{ $t('plan.jam.jamExpiredDateDesc') }}</span>
          </div>
          <content-box class="jam-detail">
            <charge-jam-chart v-if="this.currentChart === 'charge'"
                              :chargedJam="jamList.charge.list"
                              :length="jamList.charge.length"
                              :sort="sort"
                              :isLoaded="isLoaded"
                              :jamValue="jamValue"
                              :serviceType="serviceType"
                              @setJamList="setJamList"
                              @show="chargeJamShow"/>
            <remain-jam-chart v-else
                              :usedJam="jamList.use.list"
                              :length="jamList.use.length"
                              :isLoaded="isLoaded"
                              :jamValue="jamValue"
                              :serviceType="serviceType"
                              @setJamList="setJamList"
                              @show="chargeJamShow"/>
          </content-box>
        </div>
        <back-to-top :refName="'scrollContent'" />

        <register-card v-if="registerData"
                       :userData="userData"
                       :registerData="registerData"
                       @onRegisterSuccess="onRegisterSuccess"
                       @onRegisterCanceled="onRegisterCanceled"
                       @onRegisterFailed="onRegisterFailed"/>

        <dimmed v-if="isShiftJam">
          <shift-jam
            @isShiftJam="isShiftJam = $event"
            @isChargingJam="isChargingJam = true, topUpJam = true"
            @moveJam="moveJam"
            :heatmapJam="applyBasketCount"
            :remainJam="totalRemainJam"
            :jamList="jamList">
          </shift-jam>
        </dimmed>

        <dimmed v-if="isChargingJam">
          <charge-pv
            :topUpJam="topUpJam"
            :remainJam="remainJam"
            :userData="userData"
            :planData="planData"
            @registerDataChange="onRegisterDataChange"
            @remainJamChange="onRemainJamChange"
            @showTopUpAlert="showTopUpAlert = $event"
            @result="TopUpResult = $event"
            @closeLayer="isChargingJam = false, topUpJam = false" />
        </dimmed>

        <dimmed v-if="showTopUpAlert">
          <alert :icon="TopUpResult ? 'icon--validation-pass' : 'icon--alert'"
                 :title="TopUpResult ? $t('plan.alert[0].title') : $t('plan.alert[1].title')"
                 :description="TopUpResult ? $t('plan.alert[0].description') : $t('plan.alert[1].description')"
                 :submit="$t('common.confirm')"
                 @onSubmit="closeTopUpAlert" />
        </dimmed>
      </div>
    </div>
  </section>
</template>

<script>
import ContentBox from '@/components/ui/ContentBox'
import BackToTop from '@/components/ui/BackToTop'
import UiButton from '@/components/ui/UiButton'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import Alert from '@/components/ui/Alert'
import ChargePv from '@/components/user-info/manage-plan/ChargePv'
import Dimmed from '@/components/ui/Dimmed'
import ChargeJamChart from '@/components/user-info/jam/ChargeJamChart'
import RemainJamChart from '../../../components/user-info/jam/RemainJamChart'
import RegisterCard from '@/components/user-info/manage-plan/RegisterCard'
import AppDialog from '@/components/ui/AppDialog'
import ShiftJam from '@/components/user-info/jam/ShiftJam'
import { create } from 'vue-modal-dialogs'

import { mapMutations, mapState } from 'vuex'
import { EventBus } from '@/libs/event-bus'
import { getRemainJam, getJamList, getJamBasketList, getExpireJamList, getBaBasket } from '@/api/jam'
import { addDays, format } from 'date-fns'
import store from '@/store'
import UiTooltip from '@/components/ui/UiTooltip.vue'
import { thousand } from '@/libs/utils'
import {
  JAM_BASKET_LIST_STATE,
  JAM_LIST_STATE,
  JAM_PAYMENT_LIST_STATE,
  PAYMENT_LIST_STATE
} from '@/libs/constants'

const openDialog = create(AppDialog)

export default {
  components: {
    UiTooltip,
    ContentBox,
    BackToTop,
    UiButton,
    Dimmed,
    ChargePv,
    ChargeJamChart,
    RemainJamChart,
    Alert,
    RegisterCard,
    UiIconTooltip,
    ShiftJam
  },
  data () {
    return {
      sort: '-regDate',
      isLoading: true,
      showBeusableCardInfo: false,
      receiptUrl: null,
      cardInfo: null,
      beusableCardInfo: null,
      paymentHistory: null,
      duration: 0,
      durationList: [
        {
          value: 0,
          text: this.$t('common.all')
        },
        {
          value: 3,
          text: this.$t('common.recentMonths', { month: '3' })
        },
        {
          value: 6,
          text: this.$t('common.recentMonths', { month: '6' })
        },
        {
          value: 12,
          text: this.$t('common.recentMonths', { month: '12' })
        },
        {
          value: 24,
          text: this.$t('common.recentMonths', { month: '24' })
        }
      ],
      page: 0,
      limit: 30,
      registerData: null,
      currentChart: 'charge',
      topUpJam: false,
      showTopUpAlert: false,
      TopUpResult: false,
      isChargingJam: false,
      remainJam: 0,
      jamList: {
        'charge': {
          'list': [],
          'length': {}
        },
        'use': {
          'list': [],
          'length': {}
        }
      },
      expireJamList: [],
      registerCallback: null,
      isShiftJam: false,
      inRemainPV: 0,
      inChargePV: 0,
      isLoaded: false,
      serviceType: [],
      jamValue: null
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData',
      'applyBasketCount',
      'totalRemainJam',
      'jamListFilter'
    ])
  },
  methods: {
    ...mapMutations(['setJamListFilter']),
    thousand,
    chargeJamShow (data) {
      let changeList = []
      if (this.currentChart === 'charge') {
        this.jamList.charge.list.map(function (item) {
          if (item.id === data.id) {
            item.chargeShow = !item.chargeShow
          }
          if (item.jamUseId === data.id) {
            item.show = !item.show
          }
          changeList.push(item)
        })
        this.jamList.charge.list = changeList
      } else if (this.currentChart === 'use') {
        this.jamList.use.list.map(function (item) {
          if (item.id === data.id) {
            item.chargeShow = !item.chargeShow
          }
          if (item.jamUseId === data.id) {
            item.show = !item.show
          }
          changeList.push(item)
        })
        this.jamList.use.list = changeList
      }
    },
    closeTopUpAlert () {
      this.showTopUpAlert = false
    },
    onPagingOptionChange (page, limit) {
      this.page = page
      this.limit = limit
    },
    onSelectChartType (value) {
      this.currentChart = value
      let jamFilter
      let paymentFilter = []
      this.sort = '-regDate'
      this.jamValue = {
        add: true,
        subtract: true
      }
      this.setJamListFilter()
      if (this.currentChart === 'charge') {
        this.setJamListFilter(Object.values(JAM_PAYMENT_LIST_STATE))
        paymentFilter = Object.values(PAYMENT_LIST_STATE)
        this.serviceType = [
          'ba', 'beusable', 'etc'
        ]
      } else {
        this.setJamListFilter(Object.values(JAM_PAYMENT_LIST_STATE))
      }

      this.setJamList(null, null, null, jamFilter, paymentFilter, this.serviceType)
    },
    onChargeJam () {
      this.isChargingJam = true
      this.topUpJam = true
    },
    moveJam (applyBasketCount, remainJam) {
      store.commit('setApplyBasketCount', applyBasketCount)
      store.commit('setTotalRemainJam', remainJam)
    },
    setBasketJamCount () {
      const param = {}
      getBaBasket(param, true).then((resp) => {
        this.inChargePV = resp.inChargePV
        this.inRemainPV = resp.inRemainPV
        store.commit('setApplyBasketCount', resp.jamCount)
      })
    },
    setRemainJam () {
      const payload = {
        user_id: this.userData.userId,
        user_num: this.userData.userNum
      }
      getRemainJam(payload, true).then(res => {
        this.remainJam = res.paidCount + res.freeCount
        store.commit('setTotalRemainJam', res.paidCount + res.freeCount)
      }).catch(err => {
        console.log(err)
      })
    },
    setJamList (paging = null, dateRange = null, sort = null, jamFilter = [], paymentFilter = [], serviceType, jamValue) {
      this.isLoaded = false
      if (serviceType) {
        this.serviceType = serviceType
      }

      if (sort) {
        this.sort = sort
      }

      if (jamValue) {
        this.jamValue = jamValue
      }

      const payload = {
        startDate: null,
        endDate: null,
        page: 0,
        limit: 30,
        order: this.sort,
        jamFilter: this.jamListFilter && this.jamListFilter.length ? this.jamListFilter : [],
        paymentFilter: paymentFilter,
        ...(this.currentChart === 'charge' ? { serviceType: this.serviceType } : {})
      }

      if (dateRange) {
        payload.startDate = dateRange.startDate
        payload.endDate = dateRange.endDate
      }
      if (paging) {
        payload.page = paging.page - 1
        payload.limit = paging.limit
      }

      if (this.currentChart === 'charge') {
        getJamList(payload, true).then(res => {
          let concatList = []
          res.list.forEach(item => {
            concatList.push(item)
            if (item.child) {
              item.child.forEach((childItem, i) => {
                childItem.child = true
                if (item.detailCount - 1 === i) {
                  childItem.last = true
                }
                concatList.push(childItem)
              })
            }
            item.child = false
          })
          this.jamList['charge']['list'] = concatList
          this.jamList['charge']['length'] = res.length

          this.isLoaded = true
        }).catch(err => {
          console.log(err)
          this.isLoaded = true
        })
      } else if (this.currentChart === 'use') {
        getJamBasketList(payload, true).then(res => {
          let concatList = []
          res.list.forEach(item => {
            concatList.push(item)
            if (item.child) {
              item.child.forEach(childItem => {
                childItem.child = true
                concatList.push(childItem)
              })
            }
            item.child = false
          })
          this.jamList['use']['list'] = concatList
          this.jamList['use']['length'] = res.length

          this.isLoaded = true
        }).catch(err => {
          console.log(err)
          this.isLoaded = true
        })
      }
    },
    setExpireJamList () {
      getExpireJamList({ expireDate: format(addDays(new Date(), 365), 'YYYY-MM-DD') }).then(res => {
        this.expireJamList = res.remainList
      }).catch(err => {
        console.log(err)
      })
    },
    onRemainJamChange () {
      this.setRemainJam()
    },
    onRegisterSuccess () {
      this.registerData = null
      if (this.registerCallback) {
        this.registerCallback()
        setTimeout(() => {
          this.registerCallback = null
        }, 100)
      }
    },

    onRegisterCanceled () {
      this.registerData = null
    },

    onRegisterFailed (result) {
      this.registerData = null

      openDialog({
        dialogType: 'plan.registerCardFailed',
        ok: this.$t('common.confirm')
      })
    },
    onRegisterDataChange (value) {
      this.registerData = value
    },
    onShift () {
      this.isShiftJam = true
    },
    onInitJamFilter () {
      let filterJamDetailList = []
      for (let key in JAM_LIST_STATE) {
        if (key === 'CANCEL_REPORT' || key === 'BUY_REPORT') {
          filterJamDetailList = filterJamDetailList.concat(JAM_LIST_STATE[key].value)
        } else {
          filterJamDetailList.push(key)
        }
      }
      return filterJamDetailList
    }
  },
  async created () {
    EventBus.$emit('onLoading', true)
    this.isLoading = true

    this.setJamListFilter(this.onInitJamFilter())

    await this.setRemainJam()

    await this.setJamList(null, null, null, this.jamListFilter, Object.values(PAYMENT_LIST_STATE), [
      'ba', 'beusable', 'etc'
    ])
    await this.setBasketJamCount()
    await this.setExpireJamList()

    EventBus.$emit('onLoading', false)
    this.isLoading = false

    setTimeout(() => {
      const action = this.$route.params.action

      if (action) {
        this.handleBillingAction(action)
      }
    }, 300)

    EventBus.$on('onActionTrigger', (triggeredAction) => {
      if (triggeredAction) {
        this.handleBillingAction(triggeredAction)
      }
    })
  },
  beforeRouteUpdate (to, from, next) {
    const action = to.params.action

    if (action) {
      this.handleBillingAction(action)
    }
    next()
  }
}
</script>

<style lang="scss" scoped="scoped">
.box-layout {
  float: left;
  width: calc(100% - 211px);
  margin-top: 40px;
}

.jam__content {
  height: 100%;
  box-sizing: border-box;

  .content__header {
    font-size: 26px;
    font-weight: 500;
    color: #444444;
    margin-left: 30px;
    margin-right: 30px;
    padding-bottom: 22px;

    h2 {
      padding-left: 10px;

      span {
        margin-left: 20px;
        font-size: 13px;
        font-weight: 500;
        font-stretch: normal;
        font-style: normal;
        line-height: 1;
        letter-spacing: normal;
        color: #767676;
      }
    }
  }

  .content__body {
    overflow-y: auto;
    height: calc(100vh - 134px);
    padding: 0 30px 0;

    article {
      margin-top: 1px;
    }

    .content--wrap {
      border-radius: 14px;

      .title {
        font-size: 15px;
        font-weight: 500;
        color: #2f2f2f;
      }
    }

    .content__top {
      display: flex;
      .jam-detail {
        display: flex;
        position: relative;
        padding: 25px;
        min-height: 240px !important;
        min-width: 320px !important;

        .jam-info__wrap {
          width: 50%;
          position: relative;
          .jam {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 32px 10px 20px 10px;
            .icon--jam--85px {
              flex-shrink: 0;
            }
            .icon--small-jam {
              width: 80px;
              height: 80px;
            }
            .count {
              display: flex;
              justify-content: center;
              align-items: center;
              min-width: 136px;
              font-weight: 500;
              color: #FC6A00;
              .count-value {
                position: relative;
                margin-right: 18px;
                .unit {
                  position: absolute;
                  bottom: 10%;
                  right: -4px;
                  font-size: 16px;
                  font-weight: 600;
                  transform: translate(100%, 0);
                }
              }
            }
          }
          .no-jam {
            text-align: center;
            padding: 30px 0px 17px 0px;
            .description {
              font-size: 13px;
              color: #FC6A00;
              line-height: 19px;
              font-weight: 500;
            }
          }
          .button--wrap {
            position: absolute;
            bottom: 9px;
            left: 50%;
            transform: translate(-50%, 0);
            .button {
              background-color: white;
              border-color: #ec0047;
              /deep/.text {
                color: #ec0047;
              }
            }
          }
        }
        .jam-heatmap__wrap {
          width: 50%;
          margin-left: 30px;
          .title {
            display: flex;
            align-items: center;
            color: #444;
            font-size: 13px;
            font-style: normal;
            font-weight: 400;
            cursor: pointer;
            justify-content: flex-end;
            .icon {
              margin-right: 5px;
            }
          }
          .heatmap-count {
            border-radius: 5px;
            margin-top: 29px;
            border: 1px solid #D7D7D7;
            background: #F4F4F4;
            padding: 15px 12px 12px 15px;
            .description {
              display: flex;
              position: relative;
              .title {
                color: #2F2F2F;
                font-size: 14px;
                font-style: normal;
                font-weight: 500;
              }
              &/deep/ .icon {
                position: absolute;
                left: 5px;
                top: -2px;
              }

              &/deep/ .bubble {
                left: 20px;
              }
            }
            .info {
              display: inline-block;
              align-items: baseline;
              justify-content: left;
              color: #FF5B8C;
              position: relative;
              padding: 1px 8px 1px 8px;
              margin: 14px 0 15px -8px;

              & .bubble {
                display: none;
              }

              &:hover {
                background: rgba(0, 0, 0, 0.06);
                border-radius: 5px;
                & .bubble {
                  display: block;
                  bottom: calc(50% + 0px);
                  left: 50%;
                  transform: translate(-50%, -50%);
                }
              }

              .amount {
                font-size: 24px;
                font-style: normal;
                font-weight: 500;
                margin-right: 4px;
              }
              .count {
                font-size: 12px;
                font-style: normal;
                font-weight: 700;
              }
            }
            .use-add {
              margin-top: 17px;
              position: relative;
              display: inline-block;
              width: 100%;
              height: 28px;
              color: #FF5B8C;

              .use {
                color: #FF5B8C;
                display: inline-block;
                position: relative;
                text-align: left;
                padding: 0 8px;
                left: -6px;

                & .bubble {
                  display: none;

                  & /deep/ .remain {
                    color: #FF5B8C;
                  }
                }

                &:hover {
                  background: rgba(0, 0, 0, 0.06);
                  border-radius: 5px;

                  & .bubble {
                    display: block;
                    bottom: calc(50% - 0px);
                    left: 50%;
                    transform: translate(-50%, -50%);
                  }
                }

                .icon {
                  margin: 6px 5px 0 0;
                  float: left;
                  position: relative;
                }

                & span {
                  position: relative;
                  display: inline-block;
                  color: #FF5B8C;
                  font-size: 12px;
                  font-style: normal;
                  font-weight: 700;
                  line-height: 28px; /* 100% */
                  float: left;

                  &.remain {
                    font-weight: 400;
                    font-size: 13px;
                    float: inherit;
                    margin-right: 5px;
                  }

                  &.total {
                    color: #444444;
                    font-weight: 400;
                    font-size: 13px;
                    float: inherit;
                  }
                }
              }
              .add {
                position: absolute;
                right: 0;
                display: inline-block;
                text-align: right;
              }
              .count {
                font-size: 12px;
                font-style: normal;
                font-weight: 700;
              }
            }
            .jam-add {
              position: relative;
              display: inline-block;
              background-color: #fff;
              border: 1px solid #bbb;
              box-sizing: border-box;
              white-space: nowrap;
              text-align: center;
              overflow: hidden;
              color: #444;
              width: 28px;
              height: 28px;
              border-radius: 20px;
              & i {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
              }
            }
          }
        }
      }

      .expiration-detail {
        margin-left: 25px;
        padding: 25px;
        .alert {
          margin-top: 15px;
          font-size: 12px;
          font-weight: 400;
          color:#ec0047;
        }
        .table {
          margin-top: 30px;
          height: 120px;
          overflow: hidden;
          table {
            width: 100%;
            border-collapse: collapse;
            border-spacing: 0;
            table-layout: fixed;
            thead {
              tr {
                border-bottom: 1px solid #BBBBBB;
                th {
                  height: 26px;
                  font-size: 13px;
                  color: #767676;
                }
              }
            }
          }
          .tbody {
            overflow-x: hidden;
            overflow-y: scroll;
            height: 100px;
            .tr {
              border-bottom: 1px solid #EBEBEB;
              .td {
                height: 50px;
                line-height: 50px;
                text-align: center;
                font-size: 13px;
                color: #2f2f2f;
                width: 500px;
              }
            }
          }
        }
        .description {
          height: 100%;
          text-align: center;
          line-height: 170px;
          font-size: 13px;
        }
      }
    }

    .content__bottom {
      .chart-type {
        margin-top: 32px;
        margin-bottom: -17px;
        position: relative;

        .jam-expired-date-desc {
          position: absolute;
          right: 20px;
          bottom: 0;
          color: #767676;
          text-align: right;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: 12px; /* 100% */
        }

        button {
          padding: 7px 3px;
          font-weight: 500;
          font-size: 15px;
          margin: 0 15px;
          color: #999999;

          &.on {
            color: #2f2f2f;
            border-bottom: 3px solid #ec0147;
          }

          &:first-child {
            margin-left: 10px;
          }
        }
      }
      .jam-detail {
        margin-top: 30px;
        padding: 16px 26px 30px 26px;

        .title {
          padding-bottom: 20px;
        }

        .table--wrap {
          .jam-detail-pagination {
            float: right;
            width: auto;
            margin-bottom: 15px;
          }

          .content__title {
            font-size: 15px;
            font-weight: 500;
            color: #2f2f2f;
            display: inline-block;
            padding: 13px 0 20px 0;
            float: left;
          }

          .ui_select {
            float: left;
            margin-left: 20px;
          }

          /deep/ thead {
            font-size: 13px;
          }

          tbody tr {
            border-bottom: 1px solid #ebebeb;

            td {
              height: 50px;
              font-size: 13px;
              line-height: 1.23;
              color: #2f2f2f;

              &.no-list {
                height: 199px;
                color: #767676;
                text-align: center;
              }

              &.data__method {
                .icon {
                  width: 80px;
                  height: 22px;
                  background-size: contain;
                  vertical-align: middle;
                  margin-right: 5px;
                }
                .card-num {
                  display: inline-block;
                  vertical-align: middle;
                }
              }

              &.data__receipt {
                .ui-btn.size--normal {
                  height: 34px;
                  font-size: 13px;
                  min-width: 80px;
                }
              }
            }
          }
        }
      }
    }
  }

  .dimmed {
    .receipt--wrap {
      @include positionXYcenter;
      text-align: center;
      padding: 30px;

      .title {
        text-align: left;
        font-size: 15px;
        font-weight: 500;
        color: #2f2f2f;
        margin-bottom: 25px;
      }

      .icon {
        position: absolute;
        top: 12px;
        right: 12px;
      }
    }

    .beusable-card--wrap {
      @include positionXYcenter;
      text-align: left;
      padding: 30px;

      .ui-btn--wrap {
        text-align: center;

        /deep/ .ui-btn {
          margin-left: 5px;
          min-width: 90px;
        }
      }

      .title {
        font-size: 15px;
        font-weight: 500;
        color: #2f2f2f;
        margin-bottom: 8px;
      }

      .description {
        font-size: 13px;
        color: #2f2f2f;
        line-height: 1.4;
      }

      .card-info--wrap {
        width: 333px;
        height: 207px;
        margin: 36px auto 40px auto;
        padding: 0 23px;
        box-sizing: border-box;
        border-radius: 7px;
        box-shadow: 0 21px 14px -11px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
        border: solid 1px #e4e4e4;
        background-color: #ffffff;
      }

      .card-number--wrap {
        margin-top: 58px;

        .value {
          @include flexXbetween;
          align-items: center;
          font-size: 15px;
          font-weight: 500;
          color: #444444;

          .card-name {
            text-align: right;
            width: 115px;
            font-size: 14px;
            line-height: 20px;

            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;

            .icon {
              width: 80px;
              height: 22px;
              background-size: contain;
            }
          }
        }
      }

      .register-date--wrap {
        margin-top: 22px;

        .value {
          margin-top: 5px;
          font-size: 14px;
          color: #2f2f2f;
        }
      }
    }
  }
}
</style>
